/** @format */

import React, { useEffect, useMemo, useState } from "react";
import Box from "@mui/material/Box";
import Tabs from "@mui/material/Tabs";
import Tab from "@mui/material/Tab";
import FormControl from "@mui/material/FormControl";
import InputLabel from "@mui/material/InputLabel";
import Select from "@mui/material/Select";
import MenuItem from "@mui/material/MenuItem";
import Button from "@mui/material/Button";
import TextField from "@mui/material/TextField";
import Checkbox from "@mui/material/Checkbox";
import FormControlLabel from "@mui/material/FormControlLabel";
import CircularProgress from "@mui/material/CircularProgress";
import CloudUploadIcon from "@mui/icons-material/CloudUpload";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import AddIcon from "@mui/icons-material/Add";
import Typography from "@mui/material/Typography";
import IconButton from "@mui/material/IconButton";
import Link from "@mui/material/Link";
import { isKeyUploadAllowed } from "./cf-v2-submitted-inputs";
import { getStepDocumentTabs } from "./cf-v2-input-layout";

const isBlank = (v) => v === undefined || v === null || v === "";

// "*DocumentDate" fields (Document Date on LOI/DDR/License Feasibility/Possession Letter)
// and any DATE field explicitly flagged `defaultToday` default to today's date rather
// than starting blank.
const isDocumentDateField = (field) => field.type === "DATE" && (/DocumentDate$/.test(field.key) || field.defaultToday);
const todayDateString = () => new Date().toISOString().slice(0, 10);

// A field applies only when values[showWhen.key] is in showWhen.valueIn (else it's hidden/inactive).
const isFieldVisible = (field, values) => {
  if (!field.showWhen || !field.showWhen.key) return true;
  return (field.showWhen.valueIn || []).includes(values[field.showWhen.key]);
};

/**
 * Shared validation for a set of inputFields against the current values. Mirrors the
 * backend `collectStepInputs`: skips fields hidden by `showWhen`, enforces `required`,
 * checks dropdown conditional uploads, and rejects non-numeric NUMBER values.
 * Returns the first error message, or null when valid.
 */
export const validateStepInputs = (fields = [], values = {}) => {    
  for (let i = 0; i < fields.length; i += 1) {
    const f = fields[i];
    if (!isFieldVisible(f, values)) continue;
    const v = values[f.key];

    if (f.type === "DROPDOWN") {
      if (f.required && isBlank(v)) return `Please select ${f.label}`;
      if (f.fileUpload && (f.fileUpload.whenValueIn || []).includes(v)) {
        const fk = f.fileUpload.key || `${f.key}File`;
        if (isBlank(values[fk])) return `Please upload ${f.fileUpload.label || f.label}`;
      }
    } else if (f.type === "UPLOAD") {
      if (f.required && isBlank(v)) return `Please upload ${f.label}`;
    } else if (f.type === "NUMBER") {
      if (f.required && isBlank(v)) return `Please enter ${f.label}`;
      if (!isBlank(v) && Number.isNaN(Number(v))) return `${f.label} must be a number`;
    } else if (f.type === "DATE" || f.type === "TEXT") {
      if (f.required && isBlank(v)) return `Please enter ${f.label}`;
    } else if (f.type === "REPEATABLE") {
      const entries = Array.isArray(v) ? v : [];
      if (f.required && !entries.length) return `Please add at least one ${f.label}`;
      for (let j = 0; j < entries.length; j += 1) {
        const entry = entries[j] || {};
        const itemErr = validateStepInputs(f.itemFields || [], entry);
        if (itemErr) return `${itemErr} (${f.label} #${j + 1})`;
      }
    }
    // BOOLEAN is never blocking — absence means "No".
  }
  return null;
};

/**
 * Renders a step's inputFields for the Approve / Edit dialogs.
 * When the step maps to more than one document (per `DOCUMENT_GROUPS`), the fields are
 * organised into **one tab per document/file type**; otherwise a single flat list is shown.
 *
 * Props:
 *   stepNumber        – the step whose inputs are being entered (drives tab layout)
 *   fields            – the step's inputFields
 *   values / onChange – controlled value map and (key, value) setter
 *   lockEditableFiles – per-key edit lock map (only when editing a submitted step)
 *   onUploadFile      – (fileKey, accept) => triggers the file picker/upload
 *   uploadingKey      – the key currently uploading (spinner)
 */
const CfV2StepInputForm = ({
  stepNumber,
  fields = [],
  values = {},
  onChange,
  lockEditableFiles = null,
  onUploadFile,
  uploadingKey = null,
}) => {
  const [activeTab, setActiveTab] = useState(0);

  const isLocked = (k) => lockEditableFiles != null && !isKeyUploadAllowed(lockEditableFiles, k);
  // Setting a field's value may hide any other field whose `showWhen` depends on it (e.g.
  // registerAgreements depends on registerAgreement). Clear those now so stale data from a
  // no-longer-visible field doesn't silently ride along in the submitted values.
  const setValue = (k, val) => {
    if (!onChange) return;
    onChange(k, val);
    fields.forEach((f) => {
      if (
        f.showWhen?.key === k &&
        !(f.showWhen.valueIn || []).includes(val) &&
        !isBlank(values[f.key])
      ) {
        onChange(f.key, undefined);
      }
    });
  };

  const tabs = useMemo(() => getStepDocumentTabs(stepNumber, fields), [stepNumber, fields]);

  useEffect(() => {
    fields.filter(isDocumentDateField).forEach((field) => {
      if (isBlank(values[field.key])) setValue(field.key, todayDateString());
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fields]);

  const renderField = (field) => {
    if (!isFieldVisible(field, values)) return null;

    if (field.type === "DROPDOWN") {
      const value = values[field.key] || "";
      const showUpload = field.fileUpload && (field.fileUpload.whenValueIn || []).includes(value);
      const fileKey = field.fileUpload?.key || `${field.key}File`;
      return (
        <Box key={field.key}>
          <FormControl fullWidth size="small">
            <InputLabel id={`input-${field.key}-label`}>{field.label}</InputLabel>
            <Select
              labelId={`input-${field.key}-label`}
              value={value}
              label={field.label}
              disabled={isLocked(field.key)}
              onChange={(e) => setValue(field.key, e.target.value)}
            >
              {(field.options || []).map((o) => (
                <MenuItem key={o} value={o}>
                  {o}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          {showUpload && (
            <Button
              sx={{ mt: 1 }}
              size="small"
              variant="outlined"
              startIcon={
                uploadingKey === fileKey ? <CircularProgress size={14} /> : <CloudUploadIcon fontSize="small" />
              }
              disabled={uploadingKey === fileKey || isLocked(fileKey)}
              onClick={() => onUploadFile(fileKey, field.fileUpload.accept)}
            >
              {values[fileKey]
                ? `Replace ${field.fileUpload.label || field.label}`
                : `Upload ${field.fileUpload.label || field.label}`}
            </Button>
          )}
        </Box>
      );
    }

    if (field.type === "UPLOAD") {
      return (
        <Box key={field.key}>
          <Button
            size="small"
            variant="outlined"
            startIcon={
              uploadingKey === field.key ? <CircularProgress size={14} /> : <CloudUploadIcon fontSize="small" />
            }
            disabled={uploadingKey === field.key || isLocked(field.key)}
            onClick={() => onUploadFile(field.key, field.accept)}
          >
            {values[field.key] ? `Replace ${field.label}` : `Upload ${field.label}`}
          </Button>
        </Box>
      );
    }

    if (field.type === "REPEATABLE") {
      const entries = Array.isArray(values[field.key]) ? values[field.key] : [];
      const locked = isLocked(field.key);

      const setEntry = (idx, itemKey, val) =>
        setValue(
          field.key,
          entries.map((e, i) => (i === idx ? { ...(e || {}), [itemKey]: val } : e))
        );
      const addEntry = () => setValue(field.key, [...entries, {}]);
      const removeEntry = (idx) =>
        setValue(field.key, entries.filter((_, i) => i !== idx));

      const renderItemField = (itemField, entry, idx) => {
        const itemVal = entry ? entry[itemField.key] : "";
        // Unique spinner/lock key so only the clicked entry's upload spins.
        const uploadKey = `${field.key}.${idx}.${itemField.key}`;

        if (itemField.type === "UPLOAD") {
          return (
            <Box key={itemField.key} sx={{ display: "flex", flexDirection: "column", gap: 0.5 }}>
              <Button
                size="small"
                variant="outlined"
                startIcon={
                  uploadingKey === uploadKey ? <CircularProgress size={14} /> : <CloudUploadIcon fontSize="small" />
                }
                disabled={uploadingKey === uploadKey || locked}
                onClick={() => onUploadFile(uploadKey, itemField.accept, (url) => setEntry(idx, itemField.key, url))}
              >
                {itemVal ? `Replace ${itemField.label}` : `Upload ${itemField.label}`}
              </Button>
              {itemVal && (
                <Link href={itemVal} target="_blank" rel="noopener" underline="hover" sx={{ fontSize: 12 }}>
                  View uploaded file
                </Link>
              )}
            </Box>
          );
        }

        const commonProps = {
          size: "small",
          fullWidth: true,
          label: itemField.label,
          required: itemField.required,
          disabled: locked,
          value: itemVal ?? "",
          onChange: (e) => setEntry(idx, itemField.key, e.target.value),
        };
        if (itemField.type === "DATE") {
          return <TextField key={itemField.key} type="date" InputLabelProps={{ shrink: true }} {...commonProps} />;
        }
        if (itemField.type === "NUMBER") {
          return <TextField key={itemField.key} type="number" {...commonProps} />;
        }
        // TEXT (and any other simple text-like sub-field)
        return <TextField key={itemField.key} {...commonProps} />;
      };

      return (
        <Box key={field.key} sx={{ gridColumn: "1 / -1" }}>
          {entries.map((entry, idx) => (
            // eslint-disable-next-line react/no-array-index-key
            <Box key={idx} sx={{ border: 1, borderColor: "divider", borderRadius: 1, p: 1.5, mb: 1.5 }}>
              <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 1 }}>
                <Typography variant="subtitle2">
                  {field.label} #{idx + 1}
                </Typography>
                <IconButton size="small" color="error" disabled={locked} onClick={() => removeEntry(idx)}>
                  <DeleteOutlineIcon fontSize="small" />
                </IconButton>
              </Box>
              <Box
                sx={{
                  display: "grid",
                  gridTemplateColumns: { xs: "minmax(0, 1fr)", sm: "repeat(2, minmax(0, 1fr))" },
                  columnGap: 2,
                  rowGap: 2,
                }}
              >
                {(field.itemFields || []).map((itemField) => renderItemField(itemField, entry, idx))}
              </Box>
            </Box>
          ))}
          <Button size="small" variant="text" startIcon={<AddIcon fontSize="small" />} disabled={locked} onClick={addEntry}>
            {entries.length ? `Add another ${field.label}` : `Add ${field.label}`}
          </Button>
        </Box>
      );
    }

    if (field.type === "DATE") {
      return (
        <Box key={field.key}>
          <TextField
            type="date"
            size="small"
            fullWidth
            label={field.label}
            required={field.required}
            disabled={isLocked(field.key)}
            InputLabelProps={{ shrink: true }}
            value={values[field.key] || ""}
            onChange={(e) => setValue(field.key, e.target.value)}
          />
        </Box>
      );
    }

    if (field.type === "NUMBER") {
      return (
        <Box key={field.key}>
          <TextField
            type="number"
            size="small"
            fullWidth
            label={field.label}
            required={field.required}
            disabled={isLocked(field.key)}
            value={values[field.key] ?? ""}
            onChange={(e) => setValue(field.key, e.target.value)}
          />
        </Box>
      );
    }

    if (field.type === "TEXT") {
      return (
        <Box key={field.key}>
          <TextField
            size="small"
            fullWidth
            label={field.label}
            required={field.required}
            disabled={isLocked(field.key)}
            value={values[field.key] || ""}
            onChange={(e) => setValue(field.key, e.target.value)}
          />
        </Box>
      );
    }

    if (field.type === "BOOLEAN") {
      return (
        <Box key={field.key} sx={{ display: "flex", alignItems: "center" }}>
          <FormControlLabel
            control={
              <Checkbox
                checked={values[field.key] === true || values[field.key] === "true"}
                disabled={isLocked(field.key)}
                onChange={(e) => setValue(field.key, e.target.checked)}
              />
            }
            label={field.label}
          />
        </Box>
      );
    }

    return null;
  };

  // Render a set of fields two-per-row (single column on narrow screens).
  const renderFieldGrid = (list) => (
    <Box
      sx={{
        display: "grid",
        gridTemplateColumns: { xs: "minmax(0, 1fr)", sm: "repeat(2, minmax(0, 1fr))" },
        columnGap: 2,
        rowGap: 2,
        mt: 2,
      }}
    >
      {list.map(renderField)}
    </Box>
  );

  // Single-document / unmapped steps → flat list (back-compat for steps 12/15/18).
  if (!tabs) {
    return renderFieldGrid(fields);
  }

  const current = Math.min(activeTab, tabs.length - 1);

  // A tab is incomplete when validating just its fields surfaces an error (covers
  // blank required scalars as well as empty/incomplete REPEATABLE lists).
  const tabIncomplete = (tab) => !!validateStepInputs(tab.fields, values);

  return (
    <Box>
      <Tabs
        value={current}
        onChange={(_, v) => setActiveTab(v)}
        variant="scrollable"
        scrollButtons="auto"
        sx={{ borderBottom: 1, borderColor: "divider" }}
      >
        {tabs.map((tab) => (
          <Tab
            key={tab.key}
            label={
              <span>
                {tab.label}
                {tabIncomplete(tab) && <span style={{ color: "#d32f2f", marginLeft: 4 }}>*</span>}
              </span>
            }
          />
        ))}
      </Tabs>
      {tabs.map((tab, idx) => (
        <Box key={tab.key} role="tabpanel" hidden={idx !== current} sx={{ pt: 1 }}>
          {idx === current && renderFieldGrid(tab.fields)}
        </Box>
      ))}
    </Box>
  );
};

export default CfV2StepInputForm;
